import { db, functions } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// ── Public (App-Check-gated callables; no auth, no direct Firestore reads) ──

// The fall registration pool, PROJECTED server-side to
// { id, dayOfWeek, startTime, endTime, duration, location } — staff notes and
// instructor never reach the public form. Deliberately a callable, not a
// timeSlots rules branch (STEP 0 #6 decision): anonymous clients never query
// the scheduling collection directly. Returns slots sorted day → time.
export async function getOpenRegistrationSlots() {
	const callable = httpsCallable(functions, 'getOpenRegistrationSlots');
	const res = await callable();
	return res.data.slots || [];
}

// Submit a fall registration REQUEST (not a booking — nothing is held or
// confirmed; Grace assigns from the admin dashboard). Mirrors submitInquiry:
// public callable, honeypot, throws on validation errors. Returns
// { ok, requestId, droppedSlotIds } — droppedSlotIds are requested times that
// were taken between browse and submit (recorded server-side; the request
// still stands on what's left).
export async function submitRegistrationRequest(payload) {
	const callable = httpsCallable(functions, 'submitRegistrationRequest');
	const res = await callable(payload);
	return res.data;
}

// ── Admin (Registration dashboard) ──────────────────────────────────────────

// All registration requests, newest first. Direct read — registrationRequests
// is admin/owner-read per firestore.rules (write:false; docs are created only
// by the submitRegistrationRequest Function). Unfiltered list is safe here:
// the rule predicate is caller-level (role), not resource-level.
export async function fetchRegistrationRequests() {
	const snap = await getDocs(collection(db, 'registrationRequests'));
	const tsToMs = (ts) => ts?.toMillis?.() ?? (ts?.seconds ? ts.seconds * 1000 : 0);
	return snap.docs
		.map((d) => ({ id: d.id, ...d.data() }))
		.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
}

// Admin/owner: place a student into an open registration slot. The server
// transaction is the race guard (first wins — a loss throws
// 'failed-precondition', refresh and re-pick). Optionally stamps the request
// child (requestId + childId) and advances the request status worked/placed.
// OMIT the optional keys rather than passing them as undefined. The callable
// client's encode() is `if (data == null) return null` — LOOSE equality — so an
// `undefined` property is put on the wire as `null`, not dropped. The server
// guard reads `requestId !== undefined`, which `null` sails past, and then
// fails the string check: "requestId must be a string when provided", HTTP 400.
//
// That is not hypothetical. Spelling this object out as
// `{ slotId, studentId, requestId, childId }` broke EVERY regular-lesson assign
// from the student profile between 2026-08-06 `008195d` and this fix — the one
// call site that never passes a request. Tests could not see it because they
// mock this function; the bug lives in the payload it builds, one layer below.
export async function assignStudentToSlot({ slotId, studentId, requestId, childId }) {
	const callable = httpsCallable(functions, 'assignStudentToSlot');
	const payload = { slotId, studentId };
	if (requestId) payload.requestId = requestId;
	if (childId) payload.childId = childId;
	const res = await callable(payload);
	return res.data;
}
